import { CheckCircle, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '../../services/authService';
import configService from '../../services/configService';

import taskService from '../../services/taskService';
import {
  ProgressStatus,
  ProjectMemberRole,
  Task,
  TaskCategory,
  TaskDropdownOption,
  TaskPriority,
  TaskUser,
} from '../../types';
import { DatePicker, Dropdown, FileUpload, Input, MultiDropdown, TextArea } from '../forms';

interface TaskDrawerProps {
  projectId: string;
  task?: Task;
  isOpen: boolean;
  onClose: () => void;
  onSave: (task: Task) => void;
  assignableUsers: TaskUser[];
  onAddMember?: () => void;
}

const TaskDrawer: React.FC<TaskDrawerProps> = ({
  projectId,
  task,
  isOpen,
  onClose,
  onSave,
  assignableUsers,
  onAddMember,
}) => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState<{
    title: string;
    description: string;
    priority: string;
    category: string;
    assignedToId: string;
    startDate: string | undefined;
    dueDate: string | undefined;
    progress: number;
    progressStatus: ProgressStatus;
    visibleToRoles: ProjectMemberRole[];
    tags: string[];
    location: string;
    dependencies: string[];
    attachments: File[];
  }>({
    title: '',
    description: '',
    priority: 'medium',
    category: 'other',
    assignedToId: '',
    startDate: undefined,
    dueDate: undefined,
    progress: 0,
    progressStatus: 'on_track',
    visibleToRoles: [
      'owner',
      'project_manager',
      'architect',
      'engineer',
      'contractor',
      'site_supervisor',
      'client',
      'viewer',
    ],
    tags: [],
    location: '',
    dependencies: [],
    attachments: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<TaskUser | null>(null);

  // Config-driven dropdown options with fallbacks
  const [priorityOptions, setPriorityOptions] = useState<TaskDropdownOption[]>([
    { value: 'low', label: 'Low Priority', isActive: true, order: 1 },
    { value: 'medium', label: 'Medium Priority', isActive: true, order: 2 },
    { value: 'high', label: 'High Priority', isActive: true, order: 3 },
    { value: 'urgent', label: 'Urgent', isActive: true, order: 4 },
  ]);
  const [categoryOptions, setCategoryOptions] = useState<TaskDropdownOption[]>([
    { value: 'design', label: 'Design', isActive: true, order: 1 },
    { value: 'construction', label: 'Construction', isActive: true, order: 2 },
    { value: 'inspection', label: 'Inspection', isActive: true, order: 3 },
    { value: 'documentation', label: 'Documentation', isActive: true, order: 4 },
    { value: 'other', label: 'Other', isActive: true, order: 5 },
  ]);
  const [roleOptions, setRoleOptions] = useState<TaskDropdownOption[]>([
    { value: 'owner', label: 'Owner', isActive: true, order: 1 },
    { value: 'project_manager', label: 'Project Manager', isActive: true, order: 2 },
    { value: 'architect', label: 'Architect', isActive: true, order: 3 },
    { value: 'engineer', label: 'Engineer', isActive: true, order: 4 },
    { value: 'contractor', label: 'Contractor', isActive: true, order: 5 },
    { value: 'site_supervisor', label: 'Site Supervisor', isActive: true, order: 6 },
    { value: 'client', label: 'Client', isActive: true, order: 7 },
    { value: 'viewer', label: 'Viewer', isActive: true, order: 8 },
  ]);

  // Load dropdown configurations using generic config service
  useEffect(() => {
    const loadConfigurations = async () => {
      try {
        const config = await configService.getTaskConfigurations();

        // Only update if we got valid arrays with content
        if (Array.isArray(config.priorities) && config.priorities.length > 0) {
          setPriorityOptions(config.priorities);
        }
        if (Array.isArray(config.categories) && config.categories.length > 0) {
          setCategoryOptions(config.categories);
        }
        if (Array.isArray(config.roles) && config.roles.length > 0) {
          setRoleOptions(config.roles);
        }
      } catch {
        // Fallback options are already set in initial state
      }
    };

    if (isOpen) {
      loadConfigurations();
    }
  }, [isOpen]);

  // Load current user for default assignment
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        // First try to get from localStorage
        const storedUser = authService.getStoredUser();
        if (storedUser) {
          // Convert User to TaskUser format
          const taskUser: TaskUser = {
            id: storedUser.id,
            firstName: storedUser.firstName || '',
            lastName: storedUser.lastName || '',
            email: storedUser.email,
            role: 'project_manager', // Default role, will be updated when assignableUsers loads
          };
          setCurrentUser(taskUser);

          // Set as default assignee if no task is being edited
          if (!task && formData.assignedToId === '') {
            handleInputChange('assignedToId', storedUser.id);
          }
        }
      } catch {
        // Error loading current user
      }
    };

    if (isOpen) {
      loadCurrentUser();
    }
  }, [isOpen, task]);

  // Update current user when assignableUsers loads
  useEffect(() => {
    if (assignableUsers.length > 0 && currentUser) {
      const userInProject = assignableUsers.find(user => user.id === currentUser.id);
      if (userInProject) {
        setCurrentUser(userInProject);
      }
    }
  }, [assignableUsers, currentUser]);

  const userOptions = [
    ...assignableUsers.map(user => ({
      value: user.id,
      label: `${user.firstName} ${user.lastName}`,
      subtitle: user.role,
    })),
    ...(onAddMember
      ? [
          {
            value: 'ADD_MEMBER',
            label: '+ Add Team Member',
            subtitle: 'Invite new member to project',
            className: 'text-red-600 font-medium border-t border-gray-200 mt-1 pt-1',
          },
        ]
      : []),
  ];

  const tagOptions = [
    { value: 'urgent', label: 'Urgent' },
    { value: 'important', label: 'Important' },
    { value: 'review', label: 'Review' },
    { value: 'documentation', label: 'Documentation' },
    { value: 'testing', label: 'Testing' },
    { value: 'bug', label: 'Bug' },
    { value: 'feature', label: 'Feature' },
    { value: 'improvement', label: 'Improvement' },
  ];

  // Initialize form when task changes
  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title,
        description: task.description,
        priority: task.priority,
        category: task.category,
        assignedToId: task.assignedToId || '',
        startDate: task.startDate
          ? new Date(task.startDate).toISOString().split('T')[0]
          : undefined,
        dueDate: task.dueDate ? new Date(task.dueDate).toISOString().split('T')[0] : undefined,

        progress: task.progress || 0,
        progressStatus: task.progressStatus || 'on_track',
        visibleToRoles: task.visibleToRoles || [
          'owner',
          'project_manager',
          'architect',
          'engineer',
          'contractor',
          'site_supervisor',
          'client',
          'viewer',
        ],
        tags: task.tags || [],
        location: task.location || '',
        dependencies: task.dependencies || [],
        attachments: [], // Note: Existing attachments would need to be loaded separately from API
      });
    } else {
      // Reset form for new task
      setFormData({
        title: '',
        description: '',
        priority: 'medium',
        category: 'other',
        assignedToId: '',
        startDate: undefined,
        dueDate: undefined,

        progress: 0,
        progressStatus: 'on_track',
        visibleToRoles: [
          'owner',
          'project_manager',
          'architect',
          'engineer',
          'contractor',
          'site_supervisor',
          'client',
          'viewer',
        ],
        tags: [],
        location: '',
        dependencies: [],
        attachments: [],
      });
    }
    setError(null);
  }, [task, isOpen]);

  const handleInputChange = (field: string, value: any) => {
    if (field === 'assignedToId' && value === 'ADD_MEMBER' && onAddMember) {
      onAddMember();
      return;
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title?.trim()) {
      setError(t('tasks.validation.titleRequired'));
      return;
    }

    if (!formData.description?.trim()) {
      setError(t('tasks.validation.descriptionRequired'));
      return;
    }

    if (!formData.assignedToId || formData.assignedToId === '') {
      setError('Please assign this task to a team member');
      return;
    }

    if (formData.visibleToRoles.length === 0) {
      setError(t('tasks.validation.visibilityRequired'));
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const taskData = {
        ...formData,
        priority: formData.priority as TaskPriority,
        category: formData.category as TaskCategory,
        startDate: formData.startDate ? new Date(formData.startDate) : undefined,
        dueDate: formData.dueDate ? new Date(formData.dueDate) : undefined,
      };

      let response;
      if (task) {
        response = await taskService.updateTask(task.id, taskData);
      } else {
        response = await taskService.createTask(projectId, taskData);
      }

      if (response.success && response.data) {
        onSave(response.data);
        onClose();
      } else {
        setError(response.message || 'Failed to save task');
      }
    } catch {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90] animate-in fade-in duration-300"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed top-6 right-6 bottom-6 z-[100] w-[60%] animate-in slide-in-from-right duration-300">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 h-full flex flex-col overflow-hidden brand-form">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 bg-white rounded-t-2xl flex-shrink-0">
            <div className="flex items-center space-x-3">
              <div className="bg-red-100 p-2 rounded-lg">
                <CheckCircle className="h-5 w-5 text-red-500" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">
                {task ? t('tasks.editTask') : t('tasks.createTask')}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-8 py-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {/* Basic Information */}
              <div className="space-y-4">
                <div>
                  <Input
                    label={t('tasks.fields.title')}
                    value={formData.title || ''}
                    onChange={e => handleInputChange('title', e.target.value)}
                    placeholder={t('tasks.fields.title')}
                    required
                    maxLength={200}
                  />
                </div>

                <div>
                  <TextArea
                    label={t('tasks.fields.description')}
                    value={formData.description}
                    onChange={value => handleInputChange('description', value)}
                    placeholder="Describe the task requirements, objectives, and deliverables..."
                    required
                    rows={4}
                    maxLength={2000}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Dropdown
                      label={t('tasks.fields.priority')}
                      value={formData.priority}
                      options={priorityOptions.map(opt => ({
                        value: opt.value,
                        label: opt.label,
                      }))}
                      onChange={value => handleInputChange('priority', value)}
                      placeholder={t('tasks.fields.priority')}
                    />
                  </div>

                  <div>
                    <Dropdown
                      label={t('tasks.fields.category')}
                      value={formData.category}
                      options={categoryOptions.map(opt => ({
                        value: opt.value,
                        label: opt.label,
                      }))}
                      onChange={value => handleInputChange('category', value)}
                      placeholder={t('tasks.fields.category')}
                    />
                  </div>
                </div>

                <div>
                  <Dropdown
                    label={t('tasks.fields.assignedTo')}
                    value={formData.assignedToId}
                    options={userOptions}
                    onChange={value => handleInputChange('assignedToId', value)}
                    placeholder={t('tasks.fields.assignedTo')}
                  />
                </div>
              </div>

              {/* Timeline */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <DatePicker
                      label="Start Date"
                      value={formData.startDate || ''}
                      onChange={date => handleInputChange('startDate', date || '')}
                      placeholder="Start Date"
                    />
                  </div>

                  <div>
                    <DatePicker
                      label="Due Date"
                      value={formData.dueDate || ''}
                      onChange={date => handleInputChange('dueDate', date || '')}
                      placeholder="Due Date"
                      minDate={formData.startDate}
                    />
                  </div>
                </div>

                {/* Progress Section - Only for editing existing tasks */}
                {task && (
                  <div className="space-y-4 pt-6 border-t border-gray-200">
                    <h4 className="text-lg font-semibold text-gray-900">Progress Tracking</h4>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('tasks.fields.progress')} ({formData.progress}%)
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={formData.progress}
                        onChange={e => handleInputChange('progress', parseInt(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-brand"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Additional Details */}
              <div className="space-y-4">
                <div>
                  <Input
                    label="Location"
                    value={formData.location}
                    onChange={e => handleInputChange('location', e.target.value)}
                    placeholder="Task location (optional)"
                    maxLength={200}
                  />
                </div>

                <div>
                  <MultiDropdown
                    label="Tags"
                    value={formData.tags || []}
                    options={tagOptions}
                    onChange={values => handleInputChange('tags', values)}
                    placeholder="Select tags (optional)"
                    maxSelections={10}
                  />
                </div>

                <div>
                  <label className="mb-4 block text-sm font-medium text-gray-700">
                    {t('tasks.fields.visibleToRoles')}
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {roleOptions.map(role => (
                      <label
                        key={role.value}
                        className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.visibleToRoles.includes(
                            role.value as ProjectMemberRole
                          )}
                          onChange={e => {
                            const updatedRoles = e.target.checked
                              ? [...formData.visibleToRoles, role.value as ProjectMemberRole]
                              : formData.visibleToRoles.filter(r => r !== role.value);
                            handleInputChange('visibleToRoles', updatedRoles);
                          }}
                          className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                        />
                        <span className="text-sm font-medium text-gray-700">{role.label}</span>
                      </label>
                    ))}
                  </div>
                  <p className="mt-4 text-xs text-gray-500 italic">
                    {t('tasks.visibility.rolesHelp')}
                  </p>
                </div>

                {/* Attachments */}
                <div>
                  <FileUpload
                    label="Attachments"
                    files={formData.attachments}
                    onChange={files => handleInputChange('attachments', files)}
                    multiple={true}
                    maxFiles={5}
                    maxSize={10}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.zip,.rar"
                    hint="Upload documents, images, or other files related to this task (max 5 files, 10MB each)"
                  />
                </div>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-8 py-6 bg-gray-50 rounded-b-2xl flex-shrink-0">
            <div className="flex items-center justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={isLoading}
                className="flex items-center rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white hover:bg-red-600 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
              >
                {isLoading && (
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                )}
                {task ? t('tasks.updateTask') : t('tasks.createTask')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default TaskDrawer;
